import { useEffect, useState } from 'react';
import { useTickets } from '../contexts/TicketsProvider';
import tickyMascot from '../assets/ticky-mascot.svg';

// Two distinct celebrations. `done` = a green, bouncy "done!"; `validation` = a
// fuchsia, side-to-side "for review!" (matches the validation column accent).
const VARIANTS = {
  done: {
    label: 'done!',
    emoji: '✓',
    accent: '#34d399',
    motion: 'ticky-hop',
    confetti: ['#34d399', '#f97316', '#fbbf24', '#60a5fa', '#ffffff'],
  },
  validation: {
    label: 'for review!',
    emoji: '🔍',
    accent: '#e879f9',
    motion: 'ticky-wiggle',
    confetti: ['#e879f9', '#f97316', '#a78bfa', '#22d3ee', '#ffffff'],
  },
};

// Cheap per-trigger randomness for confetti (browser Math.random is fine here).
function makeConfetti(colors) {
  return Array.from({ length: 16 }, (_, i) => ({
    id: i,
    left: Math.round(Math.random() * 100),
    delay: +(Math.random() * 0.3).toFixed(2),
    dur: +(0.9 + Math.random() * 0.7).toFixed(2),
    rot: Math.round((Math.random() - 0.5) * 720),
    drift: Math.round((Math.random() - 0.5) * 60),
    size: 6 + Math.round(Math.random() * 5),
    color: colors[i % colors.length],
  }));
}

export default function Celebration() {
  const { celebration } = useTickets();
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!celebration) return undefined;
    const variant = VARIANTS[celebration.status];
    if (!variant) return undefined;
    setActive({ ...celebration, variant, pieces: makeConfetti(variant.confetti) });
    const t = setTimeout(() => setActive(null), 2100);
    return () => clearTimeout(t);
  }, [celebration?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;
  const { variant, pieces } = active;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex justify-center" aria-hidden="true">
      <div key={active.key} className="ticky-celebrate relative mb-12 flex flex-col items-center">
        {/* confetti burst */}
        <div className="absolute inset-x-0 -top-6 h-24">
          {pieces.map((p) => (
            <span
              key={p.id}
              className="ticky-confetti absolute top-0 block rounded-[2px]"
              style={{
                left: `${p.left}%`,
                width: p.size,
                height: p.size,
                background: p.color,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.dur}s`,
                '--rot': `${p.rot}deg`,
                '--drift': `${p.drift}px`,
              }}
            />
          ))}
        </div>

        {/* speech bubble */}
        <div
          className="ticky-pulse mb-1 rounded-full border bg-card px-3 py-1 font-mono text-sm font-semibold shadow-md"
          style={{ borderColor: variant.accent, color: variant.accent }}
        >
          {variant.emoji} {variant.label}
        </div>

        {/* the mascot, doing its status-specific move */}
        <img src={tickyMascot} alt="" className={`${variant.motion} h-20 w-20 drop-shadow-xl`} />
      </div>
    </div>
  );
}
